import { buildFacetQueryParams } from "modules/collections/details/utils/buildFiltersFromParams";
import type { TradeListType } from "modules/tradeLists/model/tradeList";
import type { FacetContract } from "modules/common/cardFacets";
import { QUERIES } from "modules/common/query";

export interface TradeBrowseQueryRequest {
  listId?: string;
  userId?: string;
  latitude?: string;
  longitude?: string;
  storeId?: number;
  type?: TradeListType;
  listType?: TradeListType;
  page: number;
  perPage: number;
  sort: string;
  order: string;
  query?: string;
}

export type TradeItemFacetQueryRequest = Omit<
  TradeBrowseQueryRequest,
  "page" | "perPage" | "sort" | "order"
> &
  Record<string, unknown> & {
    includeAppraisal?: boolean;
  };

function appendFacetParams(
  params: URLSearchParams,
  req: TradeItemFacetQueryRequest,
  facetContract: FacetContract
) {
  const facetParams = new URLSearchParams(
    buildFacetQueryParams(
      req as Parameters<typeof buildFacetQueryParams>[0],
      facetContract
    )
  );
  facetParams.forEach((value, key) => params.append(key, value));
}

/**
 * Canonical serializer for `/trades/browse/facets`. The endpoint never
 * receives pagination or ordering, so those fields cannot fragment its React
 * Query cache when a card grid changes page or sort order.
 */
export function buildTradeItemFacetQuery<T extends TradeItemFacetQueryRequest>(
  req: T,
  facetContract: FacetContract
): string {
  const params = new URLSearchParams();

  if (req.listId) params.set("list_id", req.listId);
  if (req.userId) params.set("user_id", req.userId);
  if (req.latitude) params.set("latitude", req.latitude);
  if (req.longitude) params.set("longitude", req.longitude);
  if (req.storeId !== undefined) params.set("store_id", String(req.storeId));

  if (req.includeAppraisal) params.set("include_appraisal", "true");

  params.set("list_type", String(req.type ?? req.listType ?? 0));
  appendFacetParams(params, req, facetContract);

  return params.toString();
}

export function cityCardFacetQueryKey(query: string) {
  return [QUERIES.CITY_CARD_FACETS, query] as const;
}

export function lgsCardFacetQueryKey(query: string) {
  return [QUERIES.LGS_CARD_FACETS, query] as const;
}

export function userCardFacetQueryKey(query: string) {
  return [QUERIES.USER_CARD_FACETS, query] as const;
}

/**
 * Canonical query-string builder shared by cached browse reads and their live
 * availability overlay. Facet parameters are produced by the same utility as
 * the rest of the browsing UI so both requests always describe the same page.
 */
export function buildBrowseQueryParams<T extends TradeBrowseQueryRequest>(
  req: T,
  facetContract: FacetContract
): string {
  const params = new URLSearchParams(
    buildFacetQueryParams(
      req as unknown as Parameters<typeof buildFacetQueryParams>[0],
      facetContract
    )
  );

  // `type` belongs to collection filters. Trade browse uses `list_type`.
  params.delete("type");

  if (req.listId) params.set("list_id", req.listId);
  if (req.userId) params.set("user_id", req.userId);
  if (req.latitude) params.set("latitude", req.latitude);
  if (req.longitude) params.set("longitude", req.longitude);
  if (req.storeId !== undefined) params.set("store_id", String(req.storeId));

  params.set("list_type", String(req.type ?? req.listType ?? 0));
  params.set("page", String(req.page));
  params.set("per_page", String(req.perPage));
  params.set("sort", req.sort);
  params.set("order", req.order);

  return params.toString();
}
